import { useInView } from '@hooks/useScroll';
import { useEffect, useState } from 'react';
import { clubInfo, type GalleryItem as StaticGalleryItem } from '@/data/chkz';
import { getGallery, loadSiteData } from '@/lib/store';
import { useSiteText } from '@/lib/SiteTextContext';

export function GallerySection() {
  const { t } = useSiteText();
  const { ref, isInView } = useInView();
  const [images, setImages] = useState<StaticGalleryItem[]>(() => getGallery());
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Load the shared site data from the DS-style backend (/api/site-data) so
  // admin edits are visible to every visitor instantly (opcja 3: live API).
  // This is the source of truth for what guests see.
  useEffect(() => {
    let alive = true;
    loadSiteData()
      .then((data) => { if (alive && data.gallery?.length) setImages(data.gallery); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Keep the displayed gallery in sync with local admin edits (same-browser
  // preview). The initial state already holds the seeded/local gallery, so we
  // only re-sync when the admin explicitly fires the local-edit event.
  useEffect(() => {
    const sync = () => setImages(getGallery());
    window.addEventListener('chkz:gallery-updated', sync);
    return () => window.removeEventListener('chkz:gallery-updated', sync);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedIndex(null);
      if (selectedIndex === null) return;
      if (e.key === 'ArrowRight') setSelectedIndex((i) => (i! + 1) % images.length);
      if (e.key === 'ArrowLeft') setSelectedIndex((i) => (i! - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIndex, images.length]);

  const selected = selectedIndex !== null ? images[selectedIndex] : null;

  return (
    <section id="gallery" className="py-20 md:py-32 bg-foam-white">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ease-sail ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
                    <p className="text-sm tracking-[0.3em] uppercase text-ocean-blue font-medium mb-4">
            {t('gallery.label')}
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-ocean-blue mb-6 text-wave">
            {t('gallery.title')}
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[240px]">
          {images.map((image, i) => (
            <div
              key={i}
              onClick={() => setSelectedIndex(i)}
              className={`group relative rounded-2xl overflow-hidden cursor-zoom-in transition-all duration-700 ease-sail ${
                isInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
              } ${i % 5 === 0 || i % 7 === 0 ? 'col-span-2 row-span-2' : ''} ${
                i % 4 === 2 ? 'row-span-2' : ''
              }`}
              style={{ transitionDelay: `${(i % 4) * 80}ms` }}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-sail group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="font-bold text-sm md:text-base">{image.caption}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-text-muted font-light text-sm">
          Więcej zdjęć na naszym{' '}
          <a href={clubInfo.instagramUrl} target="_blank" rel="noreferrer" className="text-ocean-blue underline">
            Instagramie
          </a>{' '}
          i{' '}
          <a href={clubInfo.facebookUrl} target="_blank" rel="noreferrer" className="text-ocean-blue underline">
            Facebooku
          </a>
        </p>
      </div>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-[fade-in_0.4s_ease-out]"
          onClick={() => setSelectedIndex(null)}
        >
          <div
            className="relative max-w-4xl max-h-[80vh] mx-4 animate-[fade-in_0.5s_cubic-bezier(0.16,1,0.3,1)]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selected.src}
              alt={selected.alt}
              className="max-w-full max-h-[80vh] rounded-lg object-contain"
            />
            <div className="mt-4 flex items-center justify-between text-white">
              <p className="text-lg">{selected.caption}</p>
              <div className="flex gap-3">
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedIndex(selectedIndex === null ? 0 : (selectedIndex - 1 + images.length) % images.length); }}
                  className="text-white hover:text-misty-blue transition-colors text-2xl px-2"
                  aria-label="Poprzednie"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedIndex(selectedIndex === null ? 0 : (selectedIndex + 1) % images.length); }}
                  className="text-white hover:text-misty-blue transition-colors text-2xl px-2"
                  aria-label="Następne"
                >
                  ›
                </button>
                <button
                  onClick={() => setSelectedIndex(null)}
                  className="text-white hover:text-misty-blue transition-colors text-2xl px-2"
                  aria-label="Zamknij"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
